import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { updateSection, type SectionLocale } from '../../api/sectionsService';

const SUPPORTED_LOCALES: Record<string, string> = {
  en: 'English',
  ar: 'Arabic',
};

export interface SectionForEdit {
  sectionId: string;
  name: string;
  displayOrder: number;
  locale?: SectionLocale;
}

interface EditSectionPopupProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  vendorId: string;
  section: SectionForEdit | null;
}

const EditSectionPopup: React.FC<EditSectionPopupProps> = ({
  open,
  onClose,
  onSuccess,
  vendorId,
  section,
}) => {
  const [sectionName, setSectionName] = useState('');
  const [displayOrder, setDisplayOrder] = useState<string>('');
  const [currentLang, setCurrentLang] = useState<string>('en');
  const [locale, setLocale] = useState<SectionLocale>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; displayOrder?: string }>({});

  useEffect(() => {
    if (open && section) {
      setDisplayOrder(String(section.displayOrder));
      const loc = section.locale ?? {};
      setLocale(loc);
      setCurrentLang('en');
      setSectionName(loc.en?.name ?? section.name ?? '');
    }
  }, [open, section]);

  const handleClose = () => {
    if (!isSubmitting) {
      setErrors({});
      onClose();
    }
  };

  const handleLanguageChange = useCallback(
    (newLang: string) => {
      const trimmedName = sectionName.trim();
      const nextLocale: SectionLocale = {
        ...locale,
        [currentLang]: trimmedName ? { name: trimmedName } : undefined,
      };
      const nextLocaleRecord = nextLocale as Record<string, { name?: string } | undefined>;
      if (!nextLocaleRecord[currentLang]?.name) delete nextLocaleRecord[currentLang];
      setLocale(nextLocale);
      setCurrentLang(newLang);
      setSectionName(nextLocaleRecord[newLang]?.name ?? '');
    },
    [currentLang, locale, sectionName]
  );

  const validateForm = (): boolean => {
    const newErrors: { name?: string; displayOrder?: string } = {};
    if (!sectionName.trim()) {
      newErrors.name = 'Section name is required';
    }
    if (!displayOrder.trim()) {
      newErrors.displayOrder = 'Display order is required';
    } else {
      const orderNum = parseInt(displayOrder, 10);
      if (isNaN(orderNum) || orderNum < 0) {
        newErrors.displayOrder = 'Display order must be a valid positive number';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!section) return;
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const finalLocale: SectionLocale = {
        ...locale,
        [currentLang]: sectionName.trim() ? { name: sectionName.trim() } : undefined,
      };
      const defaultName =
        (finalLocale.en?.name ?? (currentLang === 'en' ? sectionName.trim() : '')) || sectionName.trim();

      const response = await updateSection(vendorId, section.sectionId, {
        name: defaultName,
        displayOrder: parseInt(displayOrder, 10),
        ...(Object.keys(finalLocale).length > 0 && { locale: finalLocale }),
      });

      if (response.success) {
        toast.success('Section updated successfully!');
        setErrors({});
        onSuccess();
        onClose();
      } else {
        toast.error(response.message || 'Failed to update section');
      }
    } catch (error: any) {
      console.error('Error updating section:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to update section. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!section) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 700,
            fontSize: '20px',
            color: '#181818',
          }}
        >
          Edit Section
        </Typography>
        <IconButton onClick={handleClose} disabled={isSubmitting}>
          <Close />
        </IconButton>
      </DialogTitle>

      <Box component="form" onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            <FormControl fullWidth size="small">
              <Select
                value={currentLang}
                onChange={(e) => handleLanguageChange(e.target.value)}
                displayEmpty
                disabled={isSubmitting}
                sx={{
                  borderRadius: '12px',
                  fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                }}
              >
                {Object.entries(SUPPORTED_LOCALES).map(([code, label]) => (
                  <MenuItem key={code} value={code}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Section Name"
              value={sectionName}
              onChange={(e) => {
                setSectionName(e.target.value);
                if (errors.name) setErrors({ ...errors, name: undefined });
              }}
              error={!!errors.name}
              helperText={errors.name}
              required
              disabled={isSubmitting}
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: '12px' },
                '& .MuiInputLabel-root': {
                  fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                },
              }}
            />

            <TextField
              fullWidth
              label="Display Order"
              type="number"
              value={displayOrder}
              onChange={(e) => {
                setDisplayOrder(e.target.value);
                if (errors.displayOrder) setErrors({ ...errors, displayOrder: undefined });
              }}
              error={!!errors.displayOrder}
              helperText={errors.displayOrder || 'Enter a number to determine the order of this section'}
              required
              disabled={isSubmitting}
              inputProps={{ min: 0, step: 1 }}
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: '12px' },
                '& .MuiInputLabel-root': {
                  fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                },
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleClose}
            disabled={isSubmitting}
            sx={{
              fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
              textTransform: 'none',
              color: '#181818',
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
              textTransform: 'none',
              backgroundColor: '#67314f',
              '&:hover': { backgroundColor: '#5a2a42' },
              minWidth: '120px',
            }}
          >
            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default EditSectionPopup;
